/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import { Component, Inject, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnInit } from "@angular/core";
import { saveAs } from "file-saver";
import { MatDialogRef } from "@angular/material/dialog";
import { ApiService } from "@app/@core/services";
import { PageService } from "@auth/services/page";
import { MAT_DIALOG_DATA } from "@angular/material";
import { AdminComplianceService } from "@app/@shared/services/admin/compliance";
import { MessageService } from "@app/@shared/components/message/message.service";
import { DatatableComponent } from "@swimlane/ngx-datatable";
import { Subscription, fromEvent } from "rxjs";
import { debounceTime, map, distinctUntilChanged, filter } from "rxjs/operators";

import * as Fuse from "fuse.js";

@Component({
  selector: "admin-bsc-tag-submission-dialog",
  templateUrl: "./bsc-tag-submission-dialog.component.html",
  styleUrls: ["./bsc-tag-submission-dialog.component.scss"],
})
export class AdminBscTagSubmissionDialogComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild("submissionsDatatable", { static: false }) submissionsDatatable: DatatableComponent;
  @ViewChild("searchInput", { static: false }) searchInput: ElementRef;

  private submissions_sub: Subscription;
  private resizeSub: Subscription;
  private postSub: Subscription;
  processing: boolean = false;
  dataLoaded: boolean = false;
  submissions;
  submissions_cache;

  searchTerm: string = null;
  searchFuse;
  searchFuseOptions = {
    shouldSort: true,
    findAllMatches: true,
    // includeMatches: true,
    threshold: 0.3,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    keys: [
      "client.display_name", 
      "cases.life_assured_name",
      "cases.product_name",
      "cases.provider_name",
      "cases.submission_category",
      "cases.product_category",
      "associate.name", 
      "client.personal.nric_no", 
      "client.personal.fin_no", 
      "client.personal.passport_no", 
      "client.business.uen", 
      "status", 
      "status_desc", 
      "date_submission"],
  };

  messages = {
    emptyMessage: "No Submission Records to display.",
    totalMessage: "Submission Records",
  };
  expanded: any = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) private injected_data: any, 
    private _notification: MessageService, 
    private pageService: PageService,
    public adminComplianceService: AdminComplianceService, 
    public dialogRef: MatDialogRef<AdminBscTagSubmissionDialogComponent>) {
      // console.log(injected_data);
      this.searchTerm = this.injected_data.sample.policy_holder_name.toUpperCase();
      this.resizeSub = this.pageService.windowResize.subscribe(() => setTimeout(() => this.submissionsDatatable && this.submissionsDatatable.recalculate()));
    }

  ngOnInit() {
    this.getSubmissions();
  }

  ngOnDestroy() {
    if (this.submissions_sub) this.submissions_sub.unsubscribe();
    if (this.resizeSub) this.resizeSub.unsubscribe();
    if (this.postSub) this.postSub.unsubscribe();
  }

  ngAfterViewInit() {
    fromEvent(this.searchInput.nativeElement, "keyup")
      .pipe(
        // get value
        map((event: any) => {
          return event.target.value;
        }),
        // if character length greater then 2
        filter((res) => res.length > 2),
        // Time in milliseconds between key events
        debounceTime(300),
        // If previous query is diffent from current
        distinctUntilChanged()
        // subscription for response
      )
      .subscribe((text: string) => {
        this.searchChange();
      });
  }

  getSubmissions() {
    if (this.injected_data) {
      this.dataLoaded = false;
      this.submissions_sub = this.adminComplianceService.getSubmissions(this.injected_data.associate.associate_uuid).subscribe((res) => {
        // console.log(res);
        let data = res.data;
        this.submissions = data;
        this.submissions_cache = data;
        this.dataLoaded = true;
        setTimeout(() => {
          this.searchChange();
          // this.submissionsDatatable.rowDetail.expandAllRows();
        })
      });
    }
  }

  getDatatableHeight(data, rowHeight) {
    let max_height = 10 * rowHeight;
    let temp_height = (data.length || 1) * rowHeight;
    return temp_height > max_height ? max_height : temp_height;
  }

  checkIntroducer(records) {
    let year = new Date().getFullYear();
    if (records.length > 0) return records.filter((row) => row["year"] == year).length > 0;
    return false;
  }

  toggleExpandRow(row) {
    this.submissionsDatatable.rowDetail.toggleExpandRow(row);
  }

  getDetailHeight(row: any, index: number): number {
    return 160;
  }

  tagSubmission(row_uuid){
    // console.log(`Row:`, row_uuid);
    // console.log(`BSC Batch:`, this.injected_data.batch);
    // console.log(`BSC Associate:`, this.injected_data.associate);
    // console.log(`Sample:`, this.injected_data.sample);
    this.processing = true;
    this.postSub = this.adminComplianceService.tagSubmission(this.injected_data.sample.id, row_uuid).subscribe(res => {
      if (res.error == false) {
        this.dialogRef.close({
          status: 'success',
          round: this.injected_data.sample.bsc_round
        });
      }
    })
  }

  fuseResults() {
    let _submissions = this.submissions_cache;
    // Filter by Search Term
    if (this.searchTerm) _submissions = this.filterBySearch(_submissions, this.searchTerm);
    this.submissions = _submissions;
  }

  filterBySearch(dataset, term = null) {
    if (term) {
      this.searchFuse = new Fuse(dataset, this.searchFuseOptions);
      return this.searchFuse.search(term);
    } else {
      return dataset;
    }
  }

  searchChange(blur = false) {
    if (!this.searchTerm) this.searchTerm = null;
    this.fuseResults();
  }
}
